import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, DollarSign, Plus, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

interface PricingSetting {
  id: string;
  name: string;
  description?: string;
  current_price: number;
  original_price: number;
  is_limited_time: boolean;
  limited_time_text: string;
  is_active: boolean;
  product_type: 'course' | 'add-on' | 'subscription' | 'lifetime';
  stripe_price_id?: string;
  stripe_product_id?: string;
  updated_at: string;
}

const AdminPricing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [products, setProducts] = useState<PricingSetting[]>([]);
  const [editingProduct, setEditingProduct] = useState<PricingSetting | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    current_price: 0,
    original_price: 0,
    is_limited_time: false,
    limited_time_text: "⚡ Limited Time!",
    is_active: true,
    product_type: "add-on" as 'course' | 'add-on' | 'subscription' | 'lifetime',
    stripe_price_id: "",
  });

  useEffect(() => {
    checkAdminAndLoadProducts();
  }, []);

  const checkAdminAndLoadProducts = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }

    const { data: roleData } = await supabase
      .from("user_roles")
      .select("*")
      .eq("user_id", session.user.id)
      .eq("role", "admin")
      .maybeSingle();

    if (!roleData) {
      toast.error("Access denied: Admin privileges required");
      navigate("/dashboard");
      return;
    }

    setIsAdmin(true);
    await loadProducts();
  };

  const loadProducts = async () => {
    try {
      const { data, error } = await supabase
        .from("pricing_settings")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      setProducts((data as PricingSetting[]) || []);
    } catch (error: any) {
      console.error("Error loading products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (product: PricingSetting) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || "",
      current_price: product.current_price / 100,
      original_price: product.original_price / 100,
      is_limited_time: product.is_limited_time,
      limited_time_text: product.limited_time_text,
      is_active: product.is_active,
      product_type: product.product_type,
      stripe_price_id: product.stripe_price_id || "",
    });
    setIsDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      description: "",
      current_price: 0,
      original_price: 0,
      is_limited_time: false,
      limited_time_text: "⚡ Limited Time!",
      is_active: true,
      product_type: "add-on",
      stripe_price_id: "",
    });
    setIsDialogOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const productData = {
        name: formData.name,
        description: formData.description,
        current_price: Math.round(formData.current_price * 100),
        original_price: Math.round(formData.original_price * 100),
        is_limited_time: formData.is_limited_time,
        limited_time_text: formData.limited_time_text,
        is_active: formData.is_active,
        product_type: formData.product_type,
        stripe_price_id: formData.stripe_price_id || null,
      };

      if (editingProduct?.id) {
        const { error } = await supabase
          .from("pricing_settings")
          .update(productData)
          .eq("id", editingProduct.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("pricing_settings")
          .insert([productData]);

        if (error) throw error;
      }

      toast.success(`Product ${editingProduct ? 'updated' : 'created'} successfully!`);
      setIsDialogOpen(false);
      await loadProducts();
    } catch (error: any) {
      console.error("Error saving product:", error);
      toast.error(error.message || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const getProductTypeColor = (type: string) => {
    const colors = {
      course: 'bg-blue-500',
      'add-on': 'bg-green-500',
      subscription: 'bg-purple-500',
      lifetime: 'bg-amber-500',
    };
    return colors[type as keyof typeof colors] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <div className="flex items-center justify-between mb-8">
          <Link to="/dashboard">
            <Button variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <Button onClick={openNewDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Add New Product
          </Button>
        </div>

        <div className="space-y-6">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-3">
              <DollarSign className="w-10 h-10 text-primary" />
              <h1 className="text-4xl font-bold">Product & Pricing Management</h1>
            </div>
            <p className="text-xl text-muted-foreground">
              Manage all products, courses, and add-ons
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <Card key={product.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <CardTitle className="text-xl">{product.name}</CardTitle>
                      <Badge className={getProductTypeColor(product.product_type)}>
                        {product.product_type}
                      </Badge>
                    </div>
                  </div>
                  {product.description && (
                    <p className="text-sm text-muted-foreground mt-2">{product.description}</p>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      {product.original_price > product.current_price && (
                        <span className="text-lg text-muted-foreground line-through">
                          ${(product.original_price / 100).toFixed(2)}
                        </span>
                      )}
                      <span className="text-3xl font-bold text-primary">
                        ${(product.current_price / 100).toFixed(2)}
                      </span>
                    </div>

                    {product.is_limited_time && (
                      <Badge variant="secondary" className="animate-pulse">
                        {product.limited_time_text}
                      </Badge>
                    )}

                    {product.stripe_price_id && (
                      <p className="text-xs text-muted-foreground font-mono">
                        {product.stripe_price_id}
                      </p>
                    )}

                    <Badge variant={product.is_active ? "default" : "secondary"}>
                      {product.is_active ? "Active" : "Inactive"}
                    </Badge>

                    <p className="text-xs text-muted-foreground">
                      Updated: {new Date(product.updated_at).toLocaleDateString()}
                    </p>
                  </div>

                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => openEditDialog(product)}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit Product
                  </Button>
                </CardContent>
              </Card>
            ))}

            {products.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="text-center py-12">
                  <p className="text-muted-foreground">No products yet. Create your first one!</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingProduct ? "Edit Product" : "Add New Product"}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <Label htmlFor="name">Product Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Email Automation Add-on"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the product"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="productType">Product Type</Label>
                <Select
                  value={formData.product_type}
                  onValueChange={(value: any) => setFormData({ ...formData, product_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="course">Course</SelectItem>
                    <SelectItem value="add-on">Add-on</SelectItem>
                    <SelectItem value="subscription">Subscription</SelectItem>
                    <SelectItem value="lifetime">Lifetime Access</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="originalPrice">Original Price ($)</Label>
                  <Input
                    id="originalPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.original_price}
                    onChange={(e) => setFormData({ ...formData, original_price: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="currentPrice">Current Price ($)</Label>
                  <Input
                    id="currentPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.current_price}
                    onChange={(e) => setFormData({ ...formData, current_price: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="stripePriceId">Stripe Price ID (optional)</Label>
                <Input
                  id="stripePriceId"
                  value={formData.stripe_price_id}
                  onChange={(e) => setFormData({ ...formData, stripe_price_id: e.target.value })}
                  placeholder="price_abc123..."
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Link this to an existing Stripe price for payments
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div className="space-y-0.5">
                    <Label htmlFor="limited-time">Limited Time Offer</Label>
                    <p className="text-sm text-muted-foreground">
                      Show animated badge
                    </p>
                  </div>
                  <Switch
                    id="limited-time"
                    checked={formData.is_limited_time}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_limited_time: checked })}
                  />
                </div>

                {formData.is_limited_time && (
                  <div>
                    <Label htmlFor="limitedTimeText">Limited Time Text</Label>
                    <Input
                      id="limitedTimeText"
                      value={formData.limited_time_text}
                      onChange={(e) => setFormData({ ...formData, limited_time_text: e.target.value })}
                      placeholder="⚡ Limited Time!"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div className="space-y-0.5">
                    <Label htmlFor="active">Active</Label>
                    <p className="text-sm text-muted-foreground">
                      Display on the site
                    </p>
                  </div>
                  <Switch
                    id="active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" disabled={saving} className="flex-1">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    editingProduct ? "Update Product" : "Create Product"
                  )}
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPricing;
